// Where the session workbench puts things: one clock every card reads on, the
// time window over it, each session's lanes bucketed onto that window, the
// machines as places holding their sessions' cards, and the wires between
// cards.
//
// The clock is broken the investigation timeline's way: the times fall into
// bursts, each burst gets room by how long it lasts (a short one still a fair
// share), and the pauses between them close to a fixed break, so a morning's
// work and a session eight days earlier both get room on one axis. A WINDOW is
// a stretch of that clock. Every card draws the same window at the same scale,
// which is what makes two sessions' timing comparable at a glance. Pure, so the
// clock, the buckets and the layout are tested without a browser.

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// The whole clock is this many units wide, whatever it spans.
export const CLOCK_U = 1000;
// A closed pause, in clock units.
export const BREAK_U = 14;
// The narrowest window, in clock units: a few buckets of a few seconds each on
// the densest clocks, and never a zero-width division.
export const MIN_WINDOW_U = 0.25;
// How far one lens can magnify its stretch.
export const LENS_MAX = 96;

export function gapText(ms) {
  if (ms < HOUR) return `${Math.max(1, Math.round(ms / MINUTE))}m`;
  if (ms < 2 * DAY) return `${Math.round(ms / HOUR)}h`;
  return `${Math.round(ms / DAY)}d`;
}

// How long a hand-off took, signed from the earlier end: "+1s", "+4m", "+3d".
export function latencyText(ms) {
  const a = Math.abs(ms);
  const s = a < MINUTE ? `${Math.round(a / SECOND)}s`
    : a < HOUR ? `${Math.round(a / MINUTE)}m`
      : a < 2 * DAY ? `${Math.round(a / HOUR)}h`
        : `${Math.round(a / DAY)}d`;
  return `+${s}`;
}

// A span's length in words: "25 min", "3 h", "8 days".
export function spanText(ms) {
  if (!(ms > 0)) return "an instant";
  if (ms < MINUTE) return `${Math.max(1, Math.round(ms / SECOND))} s`;
  if (ms < HOUR) return `${Math.round(ms / MINUTE)} min`;
  if (ms < 2 * DAY) {
    const h = ms / HOUR;
    return `${h < 10 ? h.toFixed(1).replace(/\.0$/, "") : Math.round(h)} h`;
  }
  return `${Math.round(ms / DAY)} days`;
}

// Per-turn wall clock: the record's messageAt where a turn has one (never
// running backward), and between those, turns placed by their order. `from`
// and `to` are when the session was first and last seen, for a record that
// stamps only some of its turns: some records stamp one turn in thirty, and
// carrying that one time forward stacked the whole session on one instant.
export function turnTimes(turns, { from = null, to = null } = {}) {
  const n = (turns || []).length;
  const anchors = [];
  let lastT = -Infinity;
  (turns || []).forEach((t, i) => {
    const x = Number.isFinite(t?.messageAt) ? t.messageAt : null;
    if (x != null && x >= lastT) {
      anchors.push([i, x]);
      lastT = x;
    }
  });
  if (Number.isFinite(from) && anchors.length && anchors[0][0] > 0 && from < anchors[0][1]) anchors.unshift([0, from]);
  if (Number.isFinite(from) && !anchors.length) anchors.push([0, from]);
  if (!anchors.length) return null;
  const tail = anchors[anchors.length - 1];
  if (Number.isFinite(to) && tail[0] < n - 1 && to > tail[1]) anchors.push([n - 1, to]);
  const out = new Array(n);
  let a = 0;
  for (let i = 0; i < n; i++) {
    while (a + 1 < anchors.length && anchors[a + 1][0] <= i) a += 1;
    const [ia, ta] = anchors[a];
    const next = anchors[a + 1];
    out[i] = i <= ia || !next ? ta : ta + ((next[1] - ta) * (i - ia)) / (next[0] - ia);
  }
  return out;
}
export const stampedShare = (turns) => ((turns || []).length ? turns.filter((t) => Number.isFinite(t?.messageAt)).length / turns.length : 0);

/**
 * The broken clock over a set of times. `at(t)` is a time's place in clock
 * units (0..CLOCK_U), `inv(u)` the time at a place. A time inside a pause maps
 * into its break, so windows and spans stay continuous across one. Repeated
 * times count toward how much room their burst gets.
 */
export function buildClock(points, { maxBursts = 14, minGap = 20 * MINUTE, gapShare = 0.002, lenses = null } = {}) {
  const all = (points || []).filter(Number.isFinite).sort((a, b) => a - b);
  const times = [...new Set(all)];
  if (!times.length) return null;
  const first = times[0];
  const last = times[times.length - 1];
  // A pause worth closing is short against the whole span; the burst cap, not
  // this floor, decides how many close. At a 1% floor a month-wide clock left
  // six-hour pauses open inside the open trace's own burst.
  const gapMin = Math.max(minGap, (last - first) * gapShare);
  const gaps = [];
  for (let i = 1; i < times.length; i++) if (times[i] - times[i - 1] > gapMin) gaps.push(i);
  // The widest pauses close first; smaller ones stay drawn at their length.
  const kept = new Set(gaps
    .sort((a, b) => (times[b] - times[b - 1]) - (times[a] - times[a - 1]))
    .slice(0, Math.max(0, maxBursts - 1)));
  const bursts = [];
  let start = first;
  for (let i = 1; i < times.length; i++) {
    if (!kept.has(i)) continue;
    bursts.push({ from: start, to: times[i - 1] });
    start = times[i];
  }
  bursts.push({ from: start, to: last });
  // Room goes half by how long a burst lasted and half by how much happened in
  // it (every point counts, repeats too: a turn is a point), with a floor so a
  // two-minute helper beside a three-hour session is still wide enough to
  // read. By length alone, a month of scattered sessions left the open trace a
  // sliver of every card.
  let k = 0;
  for (const b of bursts) {
    b.n = 0;
    while (k < all.length && all[k] <= b.to) {
      if (all[k] >= b.from) b.n += 1;
      k += 1;
    }
  }
  const total = bursts.reduce((n, b) => n + (b.to - b.from), 0) || 1;
  const count = bursts.reduce((n, b) => n + b.n, 0) || 1;
  const floor = 0.3 / bursts.length;
  const burstW = bursts.map((b) => Math.max(floor, 0.5 * ((b.to - b.from) / total) + 0.5 * (b.n / count)));
  const room = CLOCK_U - (bursts.length - 1) * BREAK_U;
  const sum = burstW.reduce((a, b) => a + b, 0) || 1;
  // Lenses: stretches the reader zoomed into. A lens multiplies the room of
  // the time inside it and ADDS that room to the clock, so everything outside
  // it keeps the scale it had: zooming into one burst of work no longer
  // stretches every other stretch along with it. A pause keeps its break.
  const lensList = (lenses || [])
    .filter((l) => Number.isFinite(l?.t0) && Number.isFinite(l?.t1) && l.t1 > l.t0 && Number(l.zoom) > 1)
    .map((l) => ({ ...l, zoom: Math.min(LENS_MAX, Number(l.zoom)) }));
  const pieces = [];
  bursts.forEach((bu, bi) => {
    const w = (room * burstW[bi]) / sum;
    const edges = [...new Set(lensList.flatMap((l) => [l.t0, l.t1]).filter((t) => t > bu.from && t < bu.to))].sort((a, b) => a - b);
    let a = bu.from;
    for (const b of [...edges, bu.to]) {
      const share = bu.to > bu.from ? (b - a) / (bu.to - bu.from) : 1;
      const mid = (a + b) / 2;
      const zoom = lensList.reduce((z, l) => (mid >= l.t0 && mid <= l.t1 && (b > a || (a >= l.t0 && a <= l.t1)) ? Math.max(z, l.zoom) : z), 1);
      pieces.push({ from: a, to: b, units: w * share * zoom, burst: bi, zoom });
      a = b;
    }
  });
  const iv = [];
  let u = 0;
  pieces.forEach((pc, i) => {
    iv.push({ t0: pc.from, t1: pc.to, u0: u, u1: u + pc.units, gap: false, burst: pc.burst, zoom: pc.zoom, first: i === 0 || pieces[i - 1].burst !== pc.burst });
    u += pc.units;
    const next = pieces[i + 1];
    if (next && next.burst !== pc.burst) {
      iv.push({ t0: pc.to, t1: next.from, u0: u, u1: u + BREAK_U, gap: true });
      u += BREAK_U;
    }
  });
  const U = u;
  // Inside a burst time runs linearly; inside a pause it runs linearly across
  // the break, so a window or a span that crosses one stays continuous.
  const at = (t) => {
    if (!Number.isFinite(t)) return null;
    if (t <= first) return iv[0].t1 > iv[0].t0 ? iv[0].u0 : (iv[0].u0 + iv[0].u1) / 2;
    if (t >= last) {
      const z = iv[iv.length - 1];
      return z.t1 > z.t0 ? z.u1 : (z.u0 + z.u1) / 2;
    }
    let lo = 0;
    let hi = iv.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (iv[mid].t1 < t) lo = mid + 1;
      else hi = mid;
    }
    const s = iv[lo];
    return s.t1 > s.t0 ? s.u0 + ((t - s.t0) / (s.t1 - s.t0)) * (s.u1 - s.u0) : (s.u0 + s.u1) / 2;
  };
  const inv = (x) => {
    if (!Number.isFinite(x)) return null;
    if (x <= iv[0].u0) return first;
    if (x >= iv[iv.length - 1].u1) return last;
    let lo = 0;
    let hi = iv.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (iv[mid].u1 < x) lo = mid + 1;
      else hi = mid;
    }
    const s = iv[lo];
    return s.u1 > s.u0 ? s.t0 + ((x - s.u0) / (s.u1 - s.u0)) * (s.t1 - s.t0) : s.t0;
  };
  const breaks = iv.filter((s) => s.gap).map((s) => ({ u0: s.u0, u1: s.u1, ms: s.t1 - s.t0, label: gapText(s.t1 - s.t0) }));
  const placedLenses = lensList.map((l) => ({ ...l, u0: at(Math.max(first, l.t0)), u1: at(Math.min(last, l.t1)) }));
  return { first, last, iv, at, inv, breaks, bursts: bursts.length, U, lenses: placedLenses };
}

export function clampWindow(win) {
  let u0 = Math.max(0, Math.min(CLOCK_U, win.u0));
  let u1 = Math.max(0, Math.min(CLOCK_U, win.u1));
  if (u1 < u0) [u0, u1] = [u1, u0];
  if (u1 - u0 < MIN_WINDOW_U) {
    const mid = Math.max(MIN_WINDOW_U / 2, Math.min(CLOCK_U - MIN_WINDOW_U / 2, (u0 + u1) / 2));
    u0 = mid - MIN_WINDOW_U / 2;
    u1 = mid + MIN_WINDOW_U / 2;
  }
  return { u0, u1 };
}

// The window over a stretch of time, padded a little so its first and last
// marks do not sit on the card edges. The padding stays inside the bursts the
// ends fall in: padding into a closed pause would stretch an hour's window
// across the days that pause stands for.
export function windowOver(clock, fromMs, toMs, pad = 0.04) {
  if (!clock) return { u0: 0, u1: CLOCK_U };
  const a = clock.at(fromMs);
  const b = clock.at(toMs);
  if (a == null || b == null) return { u0: 0, u1: CLOCK_U };
  const w = Math.max(MIN_WINDOW_U, b - a);
  const home = (u) => clock.iv.find((s) => !s.gap && u >= s.u0 - 1e-9 && u <= s.u1 + 1e-9) || null;
  const ha = home(a);
  const hb = home(b);
  const u0 = ha ? Math.max(ha.u0, a - w * pad) : a - w * pad;
  const u1 = hb ? Math.min(hb.u1, b + w * pad) : b + w * pad;
  return clampWindow({ u0, u1 });
}

// Which of the window's buckets a clock place falls in, or where it fell off.
export function bucketAt(u, win, n) {
  if (u == null) return { bucket: null, side: null };
  const f = (u - win.u0) / (win.u1 - win.u0);
  if (f < 0) return { bucket: null, side: "before" };
  if (f >= 1) return f === 1 ? { bucket: n - 1, side: null } : { bucket: null, side: "after" };
  return { bucket: Math.min(n - 1, Math.floor(f * n)), side: null };
}

/**
 * One session's lanes on the window: cells re-keyed from turns to the
 * window's buckets (a bucket's items are every turn's items that fell in it),
 * with the turns each bucket stands for kept beside, so a pick on a bucket
 * reads a real turn. Lanes with nothing in the window stay, empty: a row that
 * vanished while scrubbing would move every row under it.
 */
export function bucketLanes(lanes, turnU, win, n) {
  const out = new Map();
  for (const lane of lanes || []) {
    const cells = new Map();
    const bucketTurns = new Map();
    for (const [t, items] of lane.cells || []) {
      const { bucket } = bucketAt(turnU[t], win, n);
      if (bucket == null) continue;
      const cur = cells.get(bucket);
      if (cur) cur.push(...items);
      else cells.set(bucket, [...items]);
      const ts = bucketTurns.get(bucket);
      if (ts) ts.push(t);
      else bucketTurns.set(bucket, [t]);
    }
    for (const ts of bucketTurns.values()) ts.sort((a, b) => a - b);
    out.set(lane.id, { ...lane, cells, turns: [...cells.keys()].sort((a, b) => a - b), bucketTurns });
  }
  return out;
}

function lanesUnder(dir) {
  const out = [...dir.lanes];
  for (const d of dir.dirs.values()) out.push(...lanesUnder(d));
  return out;
}

// Merged cells for a folded row, bucket-keyed like its lanes.
function mergeBuckets(lanes) {
  const byBucket = new Map();
  for (const lane of lanes) {
    for (const [b, items] of lane.cells) {
      const cur = byBucket.get(b);
      if (cur) cur.push(...items);
      else byBucket.set(b, [...items]);
    }
  }
  return byBucket;
}

const NONE = new Set();

/**
 * A card's rows, opened one step at a time. Each group is one row folded to
 * its merged marks. A group the reader opens shows its first level only -- the
 * files group its top directories and files, each directory folded until it is
 * opened in turn -- and no level lists more than `cap` rows before a "+k more"
 * row that opens the rest. Opening one thing never opens another: opening the
 * files drawer used to unfold the whole tree and push the canvas off screen.
 *
 * `only` limits a card to some groups (a joined trace shows the rows its joins
 * land on); the rest fold into one "+k more groups" row that still carries
 * their marks. `keep` (lane ids) narrows a group holding any of them to the
 * rows that hold them, their directories open, the rest of each level in its
 * "+k more" row. `caps` (group id to a count) opens a group part way, to its
 * first that many rows, the rest in its "+k more" row. Every row is compact:
 * many sessions share one screen, and the card's axis already draws what a
 * lane row's sparkline would. `lanesById` maps a lane id to the lane as drawn
 * (bucketed) or, for sizing, its shape.
 */
export function cardRows({ model, lanesById, openGroups = NONE, openDirs = NONE, showAll = NONE, only = null, keep = null, caps = null, cap = 8 }) {
  const rows = [];
  const hidden = [];
  let hiddenGroups = 0;
  const head = (id, label, lanes, depth, open, extra = null) => ({
    type: "head", id, label, count: lanes.length, depth, compact: true, closed: !open,
    cells: open ? null : mergeBuckets(lanes), laneIds: open ? null : lanes.map((l) => l.id), ...extra,
  });
  // One level's rows, the first `cap` of them and a row that stands for the
  // rest; narrowed (`kept`), the ones holding a kept lane and a row for the
  // rest.
  const level = (parentId, items, depth, emit, kept = null) => {
    const all = showAll.has(parentId);
    const shown = all ? items : kept ? items.filter((it) => it.lanes.some((l) => kept.has(l.id))) : items.slice(0, caps?.get(parentId) ?? cap);
    for (const it of shown) emit(it);
    if (all || shown.length === items.length) return;
    const rest = items.filter((it) => !shown.includes(it));
    rows.push(head(`more:${parentId}`, `+${rest.length} more`, rest.flatMap((it) => it.lanes), depth, false, { more: parentId }));
  };
  for (const group of model?.groups || []) {
    const lanes = (group.lanes || []).map((l) => lanesById.get(l.id)).filter(Boolean);
    if (!lanes.length) continue;
    if (only && !only.has(group.id)) {
      hidden.push(...lanes);
      hiddenGroups += 1;
      continue;
    }
    // A group whose one lane carries the group's own name (a lane with no
    // subject of its own takes its domain's) is drawn as that lane: opened, it
    // would only say its name again under itself.
    if (lanes.length === 1 && group.kind !== "files" && lanes[0].label === group.label) {
      rows.push({ type: "lane", lane: lanes[0], depth: 0, compact: true });
      continue;
    }
    const open = openGroups.has(group.id);
    rows.push(head(group.id, group.label, lanes, 0, open));
    if (!open) continue;
    const kept = keep && lanes.some((l) => keep.has(l.id)) ? keep : null;
    if (group.kind === "files" && group.tree) {
      const walk = (node, depth, parentId) => {
        const items = [];
        for (const dir of node.dirs.values()) {
          const dl = lanesUnder(dir).map((l) => lanesById.get(l.id)).filter(Boolean);
          if (dl.length) items.push({ dir, lanes: dl });
        }
        for (const lane of node.lanes) {
          const r = lanesById.get(lane.id);
          if (r) items.push({ lane: r, lanes: [r] });
        }
        level(parentId, items, depth, (it) => {
          if (it.lane) {
            rows.push({ type: "lane", lane: it.lane, depth, compact: true });
            return;
          }
          const id = `dir:${it.dir.path}`;
          const dOpen = openDirs.has(id) || (!!kept && it.lanes.some((l) => kept.has(l.id)));
          rows.push(head(id, it.dir.name, it.lanes, depth, dOpen));
          if (dOpen) walk(it.dir, depth + 1, id);
        }, kept);
      };
      walk(group.tree, 1, group.id);
    } else {
      level(group.id, lanes.map((l) => ({ lane: l, lanes: [l] })), 1, (it) => rows.push({ type: "lane", lane: it.lane, depth: 1, compact: true }), kept);
    }
  }
  if (hidden.length) {
    rows.push(head("groups:more", `+${hiddenGroups} group${hiddenGroups === 1 ? "" : "s"}`, hidden, 0, false, { moreGroups: true }));
  }
  return rows;
}

// How many rows a group's first level holds: its top directories and files,
// or its lanes.
const firstLevelOf = (g) => (g.kind === "files" && g.tree
  ? [...g.tree.dirs.values()].filter((d) => lanesUnder(d).length).length + g.tree.lanes.length
  : g.lanes.length);

/**
 * The rows a card shows in a height the reader gave it. Its groups open
 * densest first while the rows still fit, and one that does not fit whole
 * opens part way: as many of its first rows as fit, the rest in its "+k more"
 * row (`caps`). With less room than every group folded to its one row, the
 * densest groups keep their rows and the rest fold into one "+k groups" row.
 * `density(group)` weighs a group; `heightOf(opts)` measures the rows
 * `cardRows` would give for `{ openGroups, only, caps }`. Returns
 * `{ openGroups, only, caps }` (`only` null when every group has its row).
 */
export function fitRowsToHeight({ model, budget, density, heightOf, cap = 8 }) {
  const ranked = (model?.groups || [])
    .filter((g) => g.lanes?.length)
    .map((g, i) => ({ g, w: density(g), i }))
    .sort((a, b) => b.w - a.w || a.i - b.i)
    .map((x) => x.g);
  const openGroups = new Set();
  const caps = new Map();
  if (heightOf({ openGroups, only: null, caps }) > budget) {
    const only = new Set();
    for (const g of ranked) {
      only.add(g.id);
      if (heightOf({ openGroups, only, caps }) > budget) only.delete(g.id);
    }
    return { openGroups, only, caps };
  }
  for (const g of ranked) {
    openGroups.add(g.id);
    if (heightOf({ openGroups, only: null, caps }) <= budget) continue;
    let fits = false;
    for (let n = Math.min(firstLevelOf(g), cap) - 1; n >= 1 && !fits; n -= 1) {
      caps.set(g.id, n);
      fits = heightOf({ openGroups, only: null, caps }) <= budget;
    }
    if (!fits) {
      caps.delete(g.id);
      openGroups.delete(g.id);
    }
  }
  return { openGroups, only: null, caps };
}

// How many lanes in a row a fit tries after one that did not fit: one more
// lane is a row, but the last of a group's takes its "+k more" row's place.
const FIT_MISSES = 8;

/**
 * The rows a card focused on what it rests on shows in a height the reader
 * gave it, in its groups. The groups holding the lanes it rests on open to
 * them (`cited.lanes`, shown at any height), every other group is its one
 * row while they fit (densest first; the rest fold into one "+k groups" row),
 * and then, while they fit, the lanes nearest the ones it rests on join them:
 * the rest of their groups', then every shown group's, each busiest first by
 * `laneDensity(lane)`, a lane opening its group to the lanes shown in it, the
 * rest in its "+k more" row. `heightOf(opts)` measures the rows `cardRows`
 * would give for `{ openGroups, only, keep }`. Returns `{ openGroups, only,
 * keep, focus: true }`.
 */
export function fitFocusToHeight({ model, cited, budget, laneDensity, heightOf }) {
  const groups = (model?.groups || []).filter((g) => g.lanes?.length);
  const keep = new Set(cited.lanes);
  const openGroups = new Set(groups.filter((g) => g.lanes.some((l) => keep.has(l.id))).map((g) => g.id));
  const busiest = (a, b) => laneDensity(b) - laneDensity(a);
  const weight = (g) => g.lanes.reduce((a, l) => a + laneDensity(l), 0);
  const others = groups.filter((g) => !openGroups.has(g.id)).sort((a, b) => weight(b) - weight(a));
  let only = null;
  if (heightOf({ openGroups, only, keep }) > budget) {
    only = new Set(openGroups);
    for (const g of others) {
      only.add(g.id);
      if (heightOf({ openGroups, only, keep }) > budget) only.delete(g.id);
    }
  }
  const groupOf = new Map();
  for (const g of groups) for (const l of g.lanes) groupOf.set(l.id, g.id);
  const near = groups.filter((g) => openGroups.has(g.id)).flatMap((g) => g.lanes).filter((l) => !keep.has(l.id)).sort(busiest);
  const far = others.filter((g) => !only || only.has(g.id)).flatMap((g) => g.lanes).sort(busiest);
  let misses = 0;
  for (const l of [...near, ...far]) {
    const g = groupOf.get(l.id);
    const opening = !openGroups.has(g);
    keep.add(l.id);
    if (opening) openGroups.add(g);
    if (heightOf({ openGroups, only, keep }) <= budget) {
      misses = 0;
      continue;
    }
    keep.delete(l.id);
    if (opening) openGroups.delete(g);
    if ((misses += 1) >= FIT_MISSES) break;
  }
  return { openGroups, only, keep, focus: true };
}

// Which group each lane of a model sits in, for the groups a join lands on.
export function groupOfLane(model) {
  const out = new Map();
  for (const g of model?.groups || []) for (const l of g.lanes || []) out.set(l.id, g.id);
  return out;
}

/**
 * The sub-agents a session spawned, and whether each one reported back: a
 * merge mark is a report that landed, an acknowledgement only is a launch the
 * session never heard back from.
 */
export function helpersOf(model, times = null) {
  const out = [];
  for (const lane of model?.lanes || []) {
    if (lane.domain !== "agents" || lane.collector) continue;
    const items = lane.items || [];
    const spawn = items.find((it) => it.op?.kind === "spawn");
    if (!spawn) continue;
    const merges = items.filter((it) => it.op?.kind === "merge");
    const report = merges.find((it) => it.op?.ok !== false) || merges[0] || null;
    const spawnTurn = spawn.turnIndex ?? lane.turns?.[0] ?? null;
    const reportTurn = report?.turnIndex ?? null;
    const at = (t) => (times && t != null && Number.isFinite(times[t]) ? times[t] : null);
    out.push({
      laneId: lane.id, label: lane.label, agentType: lane.agentType || null,
      spawnTurn, reportTurn, reported: !!report, failed: report?.op?.ok === false,
      spawnAt: at(spawnTurn), reportAt: at(reportTurn),
      // What it was asked (sealed when the provider encrypted the task), and
      // each result that reached the trace: a report carried in a message of
      // its own, or (report null) the result of the call that started it,
      // at that block.
      prompt: lane.spawnPrompt || "", sealed: !!lane.spawnToken,
      results: merges.map((it) => ({
        turn: it.turnIndex ?? null, block: it.blockIndex ?? null,
        report: typeof it.op?.report === "string" ? it.op.report : null,
        ok: it.op?.ok !== false,
      })),
    });
  }
  return out.sort((a, b) => (a.spawnTurn ?? 0) - (b.spawnTurn ?? 0));
}

/**
 * The machines as regions, their cards in columns, the regions packed in
 * shelves toward the viewport's shape. Cards take columns round-robin rather
 * than shortest-first, so a card that grows (its record arrived, or it was
 * opened) pushes only the cards under it, never reshuffles its neighbours.
 *
 * machines: [{ id, cards: [{ key, h }], footH }] in reading order.
 */
export function layoutMachines(machines, {
  cardW, gap = 18, pad = 16, headH = 40, shelfGap = 40, aspect = 1.8, maxCols = 3,
} = {}) {
  const regions = machines.map((m) => {
    const n = m.cards.length;
    const cols = Math.max(1, Math.min(maxCols, n <= 1 ? 1 : n <= 4 ? 2 : 3));
    const colH = new Array(cols).fill(0);
    const cards = m.cards.map((c, i) => {
      const col = i % cols;
      const pos = { key: c.key, x: pad + col * (cardW + gap), y: headH + colH[col], w: cardW, h: c.h };
      colH[col] += c.h + gap;
      return pos;
    });
    const w = pad * 2 + cols * cardW + (cols - 1) * gap;
    const h = headH + Math.max(0, Math.max(...colH) - gap) + pad + (m.footH || 0);
    return { id: m.id, w, h, cards, cols, footY: h - pad - (m.footH || 0) };
  });
  if (!regions.length) return { regions: [], w: 0, h: 0 };
  const area = regions.reduce((n, r) => n + (r.w + shelfGap) * (r.h + shelfGap), 0);
  const widest = Math.max(...regions.map((r) => r.w));
  const targetW = Math.max(widest, Math.sqrt(area * aspect));
  let x = 0;
  let y = 0;
  let shelfH = 0;
  let maxW = 0;
  for (const r of regions) {
    if (x > 0 && x + r.w > targetW) {
      x = 0;
      y += shelfH + shelfGap;
      shelfH = 0;
    }
    r.x = x;
    r.y = y;
    for (const c of r.cards) {
      c.x += x;
      c.y += y;
    }
    x += r.w + shelfGap;
    shelfH = Math.max(shelfH, r.h);
    maxW = Math.max(maxW, r.x + r.w);
  }
  return { regions, w: maxW, h: y + shelfH };
}

/**
 * A wire between two cards: a leader from each cell to its card's edge along
 * the cell's row, and a curve between the edges. Cards side by side join at
 * their facing edges; cards stacked in one column loop out to the right.
 * a, b: { x, y, card: { x, y, w, h } }.
 */
export function wireRoute(a, b) {
  const ac = a.card;
  const bc = b.card;
  let ax;
  let bx;
  let bend;
  if (bc.x >= ac.x + ac.w) {
    ax = ac.x + ac.w;
    bx = bc.x;
    bend = Math.max(30, (bx - ax) / 2);
    return { a: { x: a.x, y: a.y }, ea: { x: ax, y: a.y }, c0: { x: ax + bend, y: a.y }, c1: { x: bx - bend, y: b.y }, eb: { x: bx, y: b.y }, b: { x: b.x, y: b.y } };
  }
  if (bc.x + bc.w <= ac.x) {
    ax = ac.x;
    bx = bc.x + bc.w;
    bend = Math.max(30, (ax - bx) / 2);
    return { a: { x: a.x, y: a.y }, ea: { x: ax, y: a.y }, c0: { x: ax - bend, y: a.y }, c1: { x: bx + bend, y: b.y }, eb: { x: bx, y: b.y }, b: { x: b.x, y: b.y } };
  }
  ax = ac.x + ac.w;
  bx = bc.x + bc.w;
  bend = 40 + Math.min(120, Math.abs(b.y - a.y) * 0.12);
  const out = Math.max(ax, bx) + bend;
  return { a: { x: a.x, y: a.y }, ea: { x: ax, y: a.y }, c0: { x: out, y: a.y }, c1: { x: out, y: b.y }, eb: { x: bx, y: b.y }, b: { x: b.x, y: b.y } };
}

// The point halfway along a route's curve (the cubic at t = 0.5).
export function routeMid(r) {
  return {
    x: 0.125 * r.ea.x + 0.375 * r.c0.x + 0.375 * r.c1.x + 0.125 * r.eb.x,
    y: 0.125 * r.ea.y + 0.375 * r.c0.y + 0.375 * r.c1.y + 0.125 * r.eb.y,
  };
}

// The view that fits a box into a viewport, with a margin, never zoomed past 1.
export function fitView(box, viewW, viewH, { margin = 28, maxZoom = 1, minZoom = 0.08 } = {}) {
  if (!(box?.w > 0) || !(box?.h > 0) || !(viewW > 0) || !(viewH > 0)) return { x: margin, y: margin, z: 1 };
  const z = Math.max(minZoom, Math.min(maxZoom, (viewW - margin * 2) / box.w, (viewH - margin * 2) / box.h));
  return { z, x: (viewW - box.w * z) / 2 - box.x * z, y: Math.max(margin, (viewH - box.h * z) / 2) - box.y * z };
}

// A pan held so the content cannot leave the viewport: past a margin it stops,
// and content smaller than the viewport stays where it is rather than drifting
// to an edge. What was scrolling up into nothing on the stacked plane.
export function clampView(view, content, viewW, viewH, margin = 60) {
  const cw = content.w * view.z;
  const ch = content.h * view.z;
  const clampAxis = (p, size, extent, origin) => {
    const lo = extent > size ? size - margin - extent : Math.min(margin, size - extent - margin);
    const hi = extent > size ? margin : Math.max(margin, size - extent - margin);
    return Math.max(lo - origin, Math.min(hi - origin, p));
  };
  return {
    z: view.z,
    x: clampAxis(view.x, viewW, cw, content.x * view.z),
    y: clampAxis(view.y, viewH, ch, content.y * view.z),
  };
}

/**
 * The focused canvas's rows: one band per machine, the open trace's first.
 * In its band the claims' badges, the trace's card and its helpers lead, then
 * each related session one line (or, opened, its card). A band past `perBand`
 * sessions counts the rest on a last line. Heights only: where things sit
 * along the clock is the axis's business, so zooming never moves a row.
 *
 * bands: [{ id, focal, claimsH, cardH, helpers, items: [{ key, h }], more }]
 */
export function layoutBands(bands, { pad = 10, lineH = 22, helperH = 24, gap = 8 } = {}) {
  let y = 0;
  const out = [];
  for (const b of bands) {
    const band = { id: b.id, y0: y, items: [], helpers: [], card: null, claims: null, more: null };
    y += pad;
    if (b.focal) {
      if (b.claimsH) {
        band.claims = { y, h: b.claimsH };
        y += b.claimsH;
      }
      band.card = { y, h: b.cardH };
      y += b.cardH + (b.helpers ? 4 : gap);
      for (let i = 0; i < (b.helpers || 0); i++) {
        band.helpers.push({ y, h: helperH });
        y += helperH;
      }
      if (b.helpers) y += gap;
    }
    for (const it of b.items) {
      const h = it.h || lineH;
      band.items.push({ key: it.key, y, h, card: !!it.h });
      y += h + (it.h ? gap : 0);
    }
    if (b.more) {
      band.more = { y, h: lineH, count: b.more };
      y += lineH;
    }
    y += pad;
    band.y1 = y;
    out.push(band);
  }
  return { bands: out, h: y };
}

// A wire from a cell of one card down (or up) to a point on another row: a
// short drop at the cell's own time to its card's edge, then a curve.
export function dropRoute(a, b, edgeY) {
  const down = b.y >= a.y;
  const ey = down ? Math.max(a.y, edgeY) : Math.min(a.y, edgeY);
  const dy = Math.abs(b.y - ey);
  const bend = Math.max(18, dy * 0.5);
  return {
    a: { x: a.x, y: a.y },
    ea: { x: a.x, y: ey },
    c0: { x: a.x, y: ey + (down ? bend : -bend) },
    c1: { x: b.x, y: b.y + (down ? -bend : bend) },
    eb: { x: b.x, y: b.y },
    b: { x: b.x, y: b.y },
  };
}

// A kept reading's turns on its card as the card reads its trace now. Two reads
// of one session can number its turns differently (the anchor's own record
// against a laid-on read of the same session, or the anchor before and after
// its helpers splice in), so a reading kept with the moments it covered is
// found again by them when its own turn numbers hold nothing of its lane.
export function readingTurns(card, r) {
  const kept = (Array.isArray(r.turns) && r.turns.length ? r.turns : [r.turn]).filter((t) => Number.isInteger(t));
  const lane = card?.model?.laneById?.get(r.lane);
  if (!lane?.cells) return { turns: kept, found: false };
  if (kept.some((t) => lane.cells.get(t)?.length)) return { turns: kept, found: true };
  const times = card.times;
  if (!times || !Number.isFinite(r.t0)) return { turns: kept, found: false };
  const t1 = Number.isFinite(r.t1) ? r.t1 : r.t0;
  const out = [];
  for (const [t, items] of lane.cells) if (items?.length && times[t] >= r.t0 - 1000 && times[t] <= t1 + 1000) out.push(t);
  out.sort((a, b) => a - b);
  return out.length ? { turns: out, found: true } : { turns: kept, found: false };
}
